import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import axiosInstance from '../../utils/axiosConfig';
import useTranslation from '../../utils/useTranslation';
import { formatDate } from '../ArticleModal';
import Reveal from '../Reveal';
import SectionViewAllLink from '../SectionViewAllLink';

export function NewsCard({ item, featured = false, delay = 0, onOpen, t, lang }) {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <Reveal
      as="article"
      className={`news-card${featured ? ' news-card--featured' : ''}`}
      delay={delay}
      role="link"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
    >
      <div className="news-card-media">
        {item.image_url ? (
          <>
            <div
              className="news-card-media-blur"
              style={{ backgroundImage: `url(${item.image_url})` }}
              aria-hidden="true"
            />
            <img src={item.image_url} alt={item.title} loading="lazy" />
          </>
        ) : (
          <div className="news-card-media-placeholder" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 5h13v14H6a2 2 0 0 1-2-2V5z" />
              <path d="M17 8h3v9a2 2 0 0 1-2 2" />
              <line x1="7" y1="8.5" x2="14" y2="8.5" />
              <line x1="7" y1="12" x2="14" y2="12" />
            </svg>
          </div>
        )}
      </div>

      <div className="news-card-content">
        {item.uploaded_date && (
          <time className="news-card-date" dateTime={item.uploaded_date}>
            {formatDate(item.uploaded_date, lang)}
          </time>
        )}
        <h3 className="news-card-title">{item.title}</h3>
        {item.overview && <p className="news-card-excerpt">{item.overview}</p>}
        <span className="news-card-cta">
          {t('read_more')}
          <svg className="news-card-cta-arrow" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </span>
      </div>
    </Reveal>
  );
}

// Every news card uses the wide "featured" shape, laid out two per row.
export function NewsFeaturedGrid({ items, onOpen, t, lang }) {
  if (!items.length) return null;
  const solo = items.length === 1;
  return (
    <div className={`news-featured-grid${solo ? ' news-featured-grid--solo' : ''}`}>
      {items.map((item, i) => (
        <NewsCard
          key={item.id}
          item={item}
          featured
          delay={(i % 2) * 80}
          onOpen={() => onOpen(item.id)}
          t={t}
          lang={lang}
        />
      ))}
    </div>
  );
}

// On the home page each type shows at most 2 rows (4 cards); a "view more"
// button links to the full listing page when there are extra items.
const HOME_NEWS_LIMIT = 4;

function NewsGroup({ id, items, onOpen, t, lang }) {
  if (!items.length) return null;
  const shown = items.slice(0, HOME_NEWS_LIMIT);
  return (
    <div className="news-subsection" id={id}>
      <NewsFeaturedGrid items={shown} onOpen={onOpen} t={t} lang={lang} />
    </div>
  );
}

export default function NewsSection({ innerRef }) {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axiosInstance
      .get('public/news/', { params: { lang } })
      .then((res) => {
        if (!cancelled) setItems(res.data?.news || []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const openItem = (id) => navigate(`/news/${id}`);
  const viewMore = (type) => navigate(`/news/list/${type}`);

  // Same section, split into two anchored groups by news type so the navbar
  // dropdown can scroll straight to "Federation news" / "Chambers news".
  const unionItems = items.filter((it) => (it.type || 'union') !== 'chambers');
  const chambersItems = items.filter((it) => (it.type || 'union') === 'chambers');
  const showUnionViewAll = !loading && unionItems.length > HOME_NEWS_LIMIT;
  const showChambersViewAll = !loading && chambersItems.length > HOME_NEWS_LIMIT;

  return (
    <section className="section alt news-section" id="news" ref={innerRef}>
      <div className="container">
        <div className="projects-head">
          <h2 className="partners-title">
            <span className="partners-title-line" aria-hidden="true" />
            <span className="partners-title-text">{t('news_type_union')}</span>
            <span className="partners-title-line" aria-hidden="true" />
          </h2>
          {showUnionViewAll && (
            <SectionViewAllLink
              label={t('news_union_view_all')}
              onClick={() => viewMore('union')}
            />
          )}
        </div>

        {loading ? (
          <div className="news-section-loading">
            <div className="spinner" />
          </div>
        ) : items.length === 0 ? (
          <p className="muted-note">{t('empty_section')}</p>
        ) : (
          <div className="news-groups">
            <NewsGroup
              id="news-union"
              items={unionItems}
              onOpen={openItem}
              t={t}
              lang={lang}
            />
            {chambersItems.length > 0 && (
              <div className="projects-head">
                <h2 className="partners-title">
                  <span className="partners-title-line" aria-hidden="true" />
                  <span className="partners-title-text">{t('news_type_chambers')}</span>
                  <span className="partners-title-line" aria-hidden="true" />
                </h2>
                {showChambersViewAll && (
                  <SectionViewAllLink
                    label={t('news_chambers_view_all')}
                    onClick={() => viewMore('chambers')}
                  />
                )}
              </div>
            )}
            <NewsGroup
              id="news-chambers"
              items={chambersItems}
              onOpen={openItem}
              t={t}
              lang={lang}
            />
          </div>
        )}
      </div>
    </section>
  );
}
